import type { AxiosResponse } from 'axios'

import type {
    CertificateApprovalDetail,
    CertificateApprovalsListResponse,
    CreateCertificateApprovalPayload,
    UpdateCertificateApprovalPayload
} from '@/api/modules/certificateApprovalsQueries'
import certificateApprovalsQueries from '@/api/modules/certificateApprovalsQueries'
import jobsQueries from '@/api/modules/jobsQueries'
import type { JobPaperCertificatesListResponseSchema, JobPapersListResponseSchema } from '@/api/schemas/jobs/jobs'
import { t } from '@/i18n'
import { notificationService } from '@/services/notificationService'
import { handleApiError } from '@/utils/apiHelpers'

async function fetchCertificateApprovals(queryString = ''): Promise<AxiosResponse<CertificateApprovalsListResponse>> {
    try {
        return await certificateApprovalsQueries.fetchCertificateApprovals(queryString)
    } catch (error) {
        handleApiError(error, t('quality.certificateApprovals.errors.fetchList'))
        throw error
    }
}

async function fetchCertificateApproval(approvalId: number | string): Promise<AxiosResponse<CertificateApprovalDetail>> {
    try {
        return await certificateApprovalsQueries.fetchCertificateApproval(approvalId)
    } catch (error) {
        handleApiError(error, t('quality.certificateApprovals.errors.fetchDetail'))
        throw error
    }
}

async function updateCertificateApproval(
    approvalId: number | string,
    payload: UpdateCertificateApprovalPayload
): Promise<AxiosResponse<CertificateApprovalDetail>> {
    try {
        const response = await certificateApprovalsQueries.updateCertificateApproval(approvalId, payload)
        notificationService.success(t('quality.certificateApprovals.notifications.saveSuccess'))
        return response
    } catch (error) {
        handleApiError(error, t('quality.certificateApprovals.errors.save'))
        throw error
    }
}

async function createCertificateApproval(
    payload: CreateCertificateApprovalPayload
): Promise<AxiosResponse<CertificateApprovalDetail>> {
    try {
        const response = await certificateApprovalsQueries.createCertificateApproval(payload)
        notificationService.success(t('quality.certificateApprovals.notifications.createSuccess'))
        return response
    } catch (error) {
        handleApiError(error, t('quality.certificateApprovals.errors.create'))
        throw error
    }
}

async function deleteCertificateApproval(approvalId: number | string): Promise<void> {
    try {
        await certificateApprovalsQueries.deleteCertificateApproval(approvalId)
        notificationService.success(t('quality.certificateApprovals.notifications.deleteSuccess'))
    } catch (error) {
        handleApiError(error, t('quality.certificateApprovals.errors.delete'))
        throw error
    }
}

async function fetchCertificateApprovalPapers(): Promise<AxiosResponse<JobPapersListResponseSchema>> {
    try {
        return await jobsQueries.fetchPapers()
    } catch (error) {
        handleApiError(error, t('quality.certificateApprovals.errors.fetchPapers'))
        throw error
    }
}

async function fetchPaperCertificates(): Promise<AxiosResponse<JobPaperCertificatesListResponseSchema>> {
    try {
        return await jobsQueries.fetchPaperCertificates()
    } catch (error) {
        handleApiError(error, t('quality.certificateApprovals.errors.fetchCertificates'))
        throw error
    }
}

export const certificateApprovalsService = {
    fetchCertificateApprovals,
    fetchCertificateApproval,
    updateCertificateApproval,
    createCertificateApproval,
    deleteCertificateApproval,
    fetchCertificateApprovalPapers,
    fetchPaperCertificates
}
